import * as R from 'remeda';
import type { StoreApi } from 'zustand';
import type {
  CanvasHandler,
  CanvasHandlerExecutionContext,
  CanvasInputEvent,
  EventOrigin,
} from './types';
import { hasOnUnmount } from './types';
import {
  createKeyInputEvent,
  createPointerInputEvent,
  createWheelInputEvent,
  isPointerEvent,
  isWheelEvent,
  resolveEventOrigin,
} from './utils';

type LockState = {
  pointerLockHandlerId: string | null;
  pointerLockPointerId: number | null;
  wheelLockHandlerId: string | null;
  wheelLockTimeoutId: number | null;
};

const getIgnoredHandlerIds = (event: CanvasInputEvent): Set<string> => {
  const { target } = event.rawEvent;
  if (!(target instanceof Element)) return new Set();

  const element = target.closest('[data-ignore-tool]');
  if (!element) return new Set();

  const ignoreAttr = element.getAttribute('data-ignore-tool');
  if (!ignoreAttr) return new Set();

  return new Set(
    ignoreAttr
      .split(',')
      .map(id => id.trim())
      .filter(Boolean)
  );
};

const shouldIgnoreHandler = (
  handlerId: string,
  ignoredIds: Set<string>
): boolean => ignoredIds.has(handlerId);

const shouldRunHandler = <TStore>(
  handler: CanvasHandler<TStore>,
  event: CanvasInputEvent,
  ctx: CanvasHandlerExecutionContext<TStore>,
  ignoredIds: Set<string>
): boolean => {
  if (shouldIgnoreHandler(handler.id, ignoredIds)) return false;
  if (handler.shouldRun && !handler.shouldRun(event, ctx)) return false;
  return true;
};

export type CanvasEventHubConfig = {
  /** Returns the SVG viewport element used to determine event origin. */
  getViewport: () => Element | null;
};

export const createCanvasEventHub = <TStore = unknown>(
  handlers: readonly CanvasHandler<TStore>[],
  store: StoreApi<TStore>,
  config: CanvasEventHubConfig
) => {
  const lockState: LockState = {
    pointerLockHandlerId: null,
    pointerLockPointerId: null,
    wheelLockHandlerId: null,
    wheelLockTimeoutId: null,
  };

  const ctx: CanvasHandlerExecutionContext<TStore> = { store };

  const clearWheelLock = () => {
    lockState.wheelLockHandlerId = null;
    if (lockState.wheelLockTimeoutId !== null) {
      clearTimeout(lockState.wheelLockTimeoutId);
      lockState.wheelLockTimeoutId = null;
    }
  };

  const clearPointerLock = () => {
    lockState.pointerLockHandlerId = null;
    lockState.pointerLockPointerId = null;
  };

  const dispatch = (event: CanvasInputEvent) => {
    const ignoredHandlerIds = getIgnoredHandlerIds(event);

    const isPointerLocked =
      lockState.pointerLockHandlerId !== null && isPointerEvent(event);
    const isWheelLocked =
      lockState.wheelLockHandlerId !== null && isWheelEvent(event);

    if (isPointerLocked) {
      const lockedHandler = handlers.find(
        h => h.id === lockState.pointerLockHandlerId
      );
      if (
        lockedHandler &&
        shouldRunHandler(lockedHandler, event, ctx, ignoredHandlerIds)
      ) {
        const result = lockedHandler.handleEvent(event, ctx);
        if (result?.endLock) {
          clearPointerLock();
        }
      }
      return;
    }

    if (isWheelLocked) {
      const lockedHandler = handlers.find(
        h => h.id === lockState.wheelLockHandlerId
      );
      if (lockedHandler) {
        lockedHandler.handleEvent(event, ctx);
        // Reset the timeout to extend the lock
        if (lockState.wheelLockTimeoutId !== null) {
          clearTimeout(lockState.wheelLockTimeoutId);
          lockState.wheelLockTimeoutId = window.setTimeout(clearWheelLock, 200);
        }
      }
      return;
    }

    R.pipe(
      handlers,
      R.filter(handler =>
        shouldRunHandler(handler, event, ctx, ignoredHandlerIds)
      ),
      R.reduce((shouldContinue, handler) => {
        if (!shouldContinue) return false;

        const result = handler.handleEvent(event, ctx);

        if (result?.startLock) {
          if (isWheelEvent(event)) {
            lockState.wheelLockHandlerId = handler.id;
            lockState.wheelLockTimeoutId = window.setTimeout(
              clearWheelLock,
              200
            );
          } else if (isPointerEvent(event)) {
            lockState.pointerLockHandlerId = handler.id;
            lockState.pointerLockPointerId = event.pointerId;
          }
        }

        if (result?.endLock) {
          if (isPointerEvent(event)) {
            clearPointerLock();
          } else if (isWheelEvent(event)) {
            clearWheelLock();
          }
        }

        return !result?.stopPropagation;
      }, true)
    );
  };

  const originOf = (event: Event): EventOrigin =>
    resolveEventOrigin(event, config.getViewport());

  const handleDomPointerEvent = (event: PointerEvent) => {
    const canvasEvent = createPointerInputEvent(event, originOf(event));
    if (canvasEvent) dispatch(canvasEvent);
  };

  const handleDomWheelEvent = (event: WheelEvent) => {
    dispatch(createWheelInputEvent(event, originOf(event)));
  };

  const handleDomKeyEvent = (
    event: KeyboardEvent,
    type: 'keyDown' | 'keyUp'
  ) => {
    dispatch(createKeyInputEvent(event, type, originOf(event)));
  };

  const mount = () => {
    const cleanups = R.pipe(
      handlers,
      R.map(handler => (handler.onMount ? handler.onMount(ctx) : undefined)),
      R.filter(
        (cleanup): cleanup is () => void => typeof cleanup === 'function'
      )
    );

    return () => {
      R.pipe(
        cleanups,
        R.forEach(cleanup => cleanup())
      );

      R.pipe(
        handlers,
        R.filter(hasOnUnmount),
        R.forEach(handler => handler.onUnmount())
      );
    };
  };

  return {
    handleDomPointerEvent,
    handleDomWheelEvent,
    handleDomKeyEvent,
    mount,
  };
};
